import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useToast } from '../context/ToastContext';
import Navigation from '../components/Navigation';
import MatchCard, { Match, PlayerAvailability } from '../components/MatchCard';
import axios from 'axios';
import { Users, Mail, Settings, Phone, Calendar, ArrowLeft, ArrowUp, ArrowDown, X, Edit, Info } from 'lucide-react';
import { createPortal } from 'react-dom';
import { formatPhoneNumber } from '../utils/phoneFormatter';
import { formatMatchType } from '../utils/formatMatchType';
import { MatchTypeIcon } from '../components/icons/MatchTypeIcon';
import { getDisplayName } from '../utils/displayName';

interface LineupPosition {
  id: number;
  match_id: number;
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  won?: boolean | null;
  is_actual_result?: boolean;
  confirmations?: any[] | null;
}

interface TeamMember {
  id: number;
  user_id: number;
  active_status: boolean;
  is_captain?: boolean;
  lineup_type_preferences?: string[] | null;
  user: {
    id: number;
    name: string;
    display_name?: string;
    tennisrecord_name?: string | null;
    email: string;
    phone?: string | null;
    gender?: 'male' | 'female' | null;
    ntrp_rating?: number | null;
  };
}

interface Team {
  id: number;
  name: string;
  league?: string;
  section?: string;
  district_area?: string;
  flight_subflight?: string;
  usta_team_id?: string;
  league_start_date?: string;
  league_end_date?: string;
  members?: TeamMember[];
  lineup_format?: {
    id: number;
    total_lines: number;
    max_sets_per_line: number;
    allow_multi_line_selection: boolean;
    positions: Array<{ position_number: number; match_type: string }>;
  } | null;
}

const TeamPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuth();
  const { setSelectedTeamId } = useTeam();
  const navigate = useNavigate();
  const toast = useToast();
  const [team, setTeam] = useState<Team | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchLineups, setMatchLineups] = useState<{ [matchId: number]: LineupPosition[] }>({});
  const [availability, setAvailability] = useState<Map<number, PlayerAvailability>>(new Map());
  const [loading, setLoading] = useState(true);
  const [isCaptain, setIsCaptain] = useState(false);
  const [myPreferences, setMyPreferences] = useState<string[]>([]);
  const [editingPreferences, setEditingPreferences] = useState(false);
  const [editPrefsForm, setEditPrefsForm] = useState<string[]>([]);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [tooltipState, setTooltipState] = useState<{ text: string; x: number; y: number } | null>(null);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (!teamId || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const teamIdNum = parseInt(teamId, 10);
        if (isNaN(teamIdNum)) {
          toast.error('Invalid team ID');
          navigate('/dashboard');
          return;
        }

        // Fetch team details, matches, and captain status in parallel
        const [teamResponse, matchesResponse] = await Promise.all([
          axios.get(`/teams/${teamIdNum}`),
          axios.get(`/matches/teams/${teamIdNum}/matches`)
        ]);

        const teamData = teamResponse.data;
        setTeam(teamData);

        // Load current user's lineup preferences
        const myMember = teamData.members?.find((m: TeamMember) => m.user_id === user.id);
        if (myMember?.lineup_type_preferences) {
          setMyPreferences(myMember.lineup_type_preferences);
        }

        // Check if user is captain
        if (user.role === 'ADMIN') {
          setIsCaptain(true);
        } else {
          // Check from team members or use the is-captain endpoint
          const userMember = teamData.members?.find((m: TeamMember) => m.user_id === user.id);
          if (userMember?.is_captain) {
            setIsCaptain(true);
          } else {
            // Double-check with endpoint
            try {
              const captainResponse = await axios.get(`/teams/${teamIdNum}/is-captain`);
              setIsCaptain(captainResponse.data.is_captain || captainResponse.data.is_admin);
            } catch {
              setIsCaptain(false);
            }
          }
        }

        // Show all matches, sorted by date (oldest to newest)
        // Past matches will be at the top, upcoming matches at the bottom
        const matches = matchesResponse.data;
        const allMatches = matches.sort((a: Match, b: Match) => {
          const dateA = new Date(a.match_date).getTime();
          const dateB = new Date(b.match_date).getTime();
          // Sort all matches oldest to newest
          return dateA - dateB;
        });

        setMatches(allMatches);

        // Fetch lineup data for match display
        const lineupPromises = allMatches.map((match: Match) =>
          axios.get(`/lineups/matches/${match.id}/lineup`)
            .then(response => ({ matchId: match.id, lineup: response.data }))
            .catch(() => ({ matchId: match.id, lineup: [] }))
        );

        const lineupResults = await Promise.all(lineupPromises);
        const lineupsMap: { [matchId: number]: LineupPosition[] } = {};
        lineupResults.forEach(result => {
          lineupsMap[result.matchId] = result.lineup;
        });
        setMatchLineups(lineupsMap);

        // Fetch user availability for matches
        if (allMatches.length > 0) {
          try {
            const availabilityResponse = await axios.get(`/availability/users/${user.id}/availability`);
            const availabilityData = availabilityResponse.data;
            const availabilityMap = new Map<number, PlayerAvailability>();
            availabilityData.forEach((avail: PlayerAvailability) => {
              availabilityMap.set(avail.match_id, avail);
            });
            setAvailability(availabilityMap);
          } catch (error) {
            console.error('Failed to fetch availability:', error);
          }
        }
      } catch (error: any) {
        console.error('Failed to fetch team data:', error);
        if (error.response?.status === 404 || error.response?.status === 403) {
          toast.error('Team not found or access denied');
          navigate('/dashboard');
        } else {
          toast.error('Failed to load team data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [teamId, user, navigate, toast]);

  const handleManageTeam = () => {
    if (!teamId) return;
    const teamIdNum = parseInt(teamId, 10);
    setSelectedTeamId(teamIdNum);
    localStorage.setItem('teamManagementActiveTab', 'strategize');
    navigate('/teams/strategize');
  };

  const handleStartEditPreferences = () => {
    setEditPrefsForm([...myPreferences]);
    setEditingPreferences(true);
  };

  const handleCancelEditPreferences = () => {
    setEditingPreferences(false);
    setEditPrefsForm([]);
  };

  const handleSavePreferences = async () => {
    if (!teamId || !user) return;
    setSavingPreferences(true);
    try {
      await axios.patch(
        `/teams/${parseInt(teamId, 10)}/members/${user.id}/lineup-preferences`,
        { lineup_type_preferences: editPrefsForm.length > 0 ? editPrefsForm : null }
      );
      setMyPreferences(editPrefsForm);
      setEditingPreferences(false);
      toast.success('Lineup preferences updated');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-6 md:p-8 text-center">
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                Team not found
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter to only active members and sort alphabetically by name
  const activeMembers = (team.members?.filter(m => m.active_status) || []).sort((a, b) => 
    getDisplayName(a.user).localeCompare(getDisplayName(b.user))
  );

  // Calculate quick stats
  const calculateQuickStats = () => {
    const now = new Date();
    const upcomingMatches = matches.filter(m => new Date(m.match_date) >= now);
    const pastMatches = matches.filter(m => new Date(m.match_date) < now);
    const rosterSize = activeMembers.length;
    
    return {
      rosterSize,
      upcomingCount: upcomingMatches.length,
      pastCount: pastMatches.length,
      totalMatches: matches.length
    };
  };

  const quickStats = calculateQuickStats();

  // Format league dates for display
  const formatLeagueDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Back to Dashboard Button */}
          <div className="mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors min-h-[44px]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
          </div>

          {/* Team Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                  {team.name}
                </h1>
                {/* League Information */}
                <div className="mt-1 space-y-1">
                  {(team.league || team.section) && (
                    <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                      {[team.league, team.section].filter(Boolean).join(' • ')}
                    </p>
                  )}
                  {(team.district_area || team.flight_subflight) && (
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      {[team.district_area, team.flight_subflight].filter(Boolean).join(' • ')}
                    </p>
                  )}
                  {team.usta_team_id && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      USTA Team ID: {team.usta_team_id}
                    </p>
                  )}
                  {(team.league_start_date || team.league_end_date) && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Season: {team.league_start_date ? formatLeagueDate(team.league_start_date) : 'TBD'} - {team.league_end_date ? formatLeagueDate(team.league_end_date) : 'TBD'}
                    </p>
                  )}
                </div>
              </div>
              {isCaptain && (
                <button
                  onClick={handleManageTeam}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 min-h-[44px]"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Team
                </button>
              )}
            </div>

            {/* Team Record and Quick Stats */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {/* Upcoming Matches */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-3 md:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-primary-500" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Upcoming</p>
                </div>
                <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {quickStats.upcomingCount}
                </p>
              </div>

              {/* Past Matches */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-3 md:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Past</p>
                </div>
                <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {quickStats.pastCount}
                </p>
              </div>

              {/* Roster Size */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-3 md:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-primary-500" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Roster</p>
                </div>
                <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {quickStats.rosterSize}
                </p>
              </div>
            </div>
          </div>

          {/* Upcoming Matches and Team Members in Two Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Matches Section - Left column on desktop */}
            <div className="lg:order-1">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Matches
              </h2>
              {matches.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-6 md:p-8 text-center">
                  <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                    No matches
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {matches.map((match) => (
                    <MatchCard
                      key={match.id}
                      match={match}
                      user={user}
                      matchLineups={matchLineups}
                      availability={availability}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Right column on desktop */}
            <div className="lg:order-2 space-y-6 md:space-y-8">
              {/* Lineup Format & Preferences — combined card */}
              {team.lineup_format && team.lineup_format.positions.length > 0 && (
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Lineup Format
                  </h2>
                  <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
                    {/* Summary stats */}
                    <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-100 dark:border-gray-700/50">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 mb-0.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Lines</span>
                            <span
                              className="inline-flex cursor-help"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipState({
                                  text: "The number of matches played on match day. For example, 5 lines means 5 unique matches are played (10 players total for doubles).",
                                  x: rect.left + rect.width / 2,
                                  y: rect.top - 10
                                });
                              }}
                              onMouseLeave={() => setTooltipState(null)}
                            >
                              <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                            </span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{team.lineup_format!.total_lines}</span>
                        </div>
                        <div className="text-center border-x border-gray-100 dark:border-gray-700/50">
                          <div className="flex items-center justify-center gap-1 mb-0.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Max Sets</span>
                            <span
                              className="inline-flex cursor-help"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipState({
                                  text: "The maximum number of sets played per line/match. Typically 3 sets (even if the 3rd set is a tie-break).",
                                  x: rect.left + rect.width / 2,
                                  y: rect.top - 10
                                });
                              }}
                              onMouseLeave={() => setTooltipState(null)}
                            >
                              <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                            </span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{team.lineup_format!.max_sets_per_line}</span>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 mb-0.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Multi-Line</span>
                            <span
                              className="inline-flex cursor-help"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipState({
                                  text: "Allows a player to appear in multiple lineups for a match",
                                  x: rect.left + rect.width / 2,
                                  y: rect.top - 10
                                });
                              }}
                              onMouseLeave={() => setTooltipState(null)}
                            >
                              <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                            </span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{team.lineup_format!.allow_multi_line_selection ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Position rows — labeled like strategize tab */}
                    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {(() => {
                        const sorted = [...team.lineup_format!.positions].sort((a, b) => a.position_number - b.position_number);
                        const typeCounts: Record<string, number> = {};
                        sorted.forEach(p => { typeCounts[p.match_type] = (typeCounts[p.match_type] || 0) + 1; });
                        const typeIndex: Record<string, number> = {};
                        return sorted.map(pos => {
                          typeIndex[pos.match_type] = (typeIndex[pos.match_type] || 0) + 1;
                          const label = typeCounts[pos.match_type] > 1
                            ? `${formatMatchType(pos.match_type)} ${typeIndex[pos.match_type]}`
                            : formatMatchType(pos.match_type);
                          return (
                            <div
                              key={pos.position_number}
                              className="flex items-center gap-2.5 px-4 py-2.5 md:px-6 md:py-3"
                            >
                              <MatchTypeIcon matchType={pos.match_type} iconSize="w-5 h-5" />
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* My Lineup Preference — inline section within same card */}
                    {(() => {
                      const myMember = team.members?.find(m => m.user_id === user?.id);
                      const allUniqueTypes = Array.from(new Set(team.lineup_format!.positions.map(p => p.match_type)));
                      const gender = myMember?.user.gender || null;
                      const genderCompatibleTypes = allUniqueTypes.filter(t => {
                        if (!gender) return true;
                        if (gender === 'male') return !t.startsWith('womens_');
                        if (gender === 'female') return !t.startsWith('mens_');
                        return true;
                      });
                      if (genderCompatibleTypes.length <= 1) return null;

                      return (
                        <>
                          <div className="border-t border-gray-100 dark:border-gray-700/50 px-4 py-3 md:px-6 md:py-4 bg-gray-50/50 dark:bg-gray-800/30">
                            <div className="flex items-center justify-between">
                              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                My Preference
                              </h3>
                              {!editingPreferences ? (
                                <button
                                  onClick={handleStartEditPreferences}
                                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                >
                                  <Edit className="w-4 h-4" />
                                  Edit
                                </button>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={handleCancelEditPreferences}
                                    disabled={savingPreferences}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSavePreferences}
                                    disabled={savingPreferences}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 min-h-[44px]"
                                  >
                                    {savingPreferences ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {!editingPreferences ? (
                            /* Read-only view */
                            myPreferences.length > 0 ? (
                              <div className="px-4 py-3 md:px-6 md:py-4 space-y-2">
                                {myPreferences.map((pref, i) => (
                                  <div
                                    key={pref}
                                    className="flex items-center gap-3 py-1.5"
                                  >
                                    <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold ${
                                      i === 0
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                    }`}>
                                      {i + 1}
                                    </span>
                                    <span className={`text-sm font-medium ${
                                      i === 0
                                        ? 'text-primary-700 dark:text-primary-400'
                                        : 'text-gray-900 dark:text-gray-100'
                                    }`}>
                                      {formatMatchType(pref)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="px-4 py-4 md:px-6 md:py-5 text-center">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  No preference set — you'll be considered for any position.
                                </p>
                              </div>
                            )
                          ) : (
                            /* Edit view */
                            <div className="p-4 md:p-6">
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                Leave blank if you have no preference. Order indicates priority (1st choice at top).
                              </p>
                              {editPrefsForm.length > 0 && (
                                <div className="space-y-2 mb-3">
                                  {editPrefsForm.map((pref, index) => (
                                    <div key={pref} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2.5">
                                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-5">{index + 1}.</span>
                                      <span className="text-sm md:text-base text-gray-900 dark:text-gray-100 flex-1">{formatMatchType(pref)}</span>
                                      <button
                                        type="button"
                                        disabled={savingPreferences || index === 0}
                                        onClick={() => {
                                          const newPrefs = [...editPrefsForm];
                                          [newPrefs[index - 1], newPrefs[index]] = [newPrefs[index], newPrefs[index - 1]];
                                          setEditPrefsForm(newPrefs);
                                        }}
                                        className={`p-1 rounded ${index === 0 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                        title="Move up"
                                      >
                                        <ArrowUp className="w-4 h-4" />
                                      </button>
                                      <button
                                        type="button"
                                        disabled={savingPreferences || index === editPrefsForm.length - 1}
                                        onClick={() => {
                                          const newPrefs = [...editPrefsForm];
                                          [newPrefs[index], newPrefs[index + 1]] = [newPrefs[index + 1], newPrefs[index]];
                                          setEditPrefsForm(newPrefs);
                                        }}
                                        className={`p-1 rounded ${index === editPrefsForm.length - 1 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                        title="Move down"
                                      >
                                        <ArrowDown className="w-4 h-4" />
                                      </button>
                                      <button
                                        type="button"
                                        disabled={savingPreferences}
                                        onClick={() => setEditPrefsForm(editPrefsForm.filter((_, i) => i !== index))}
                                        className="p-1 rounded text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400"
                                        title="Remove"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {(() => {
                                const availableTypes = genderCompatibleTypes.filter(t => !editPrefsForm.includes(t));
                                if (availableTypes.length === 0) return null;
                                return (
                                  <select
                                    value=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        setEditPrefsForm([...editPrefsForm, e.target.value]);
                                      }
                                    }}
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    disabled={savingPreferences}
                                  >
                                    <option value="">Add preference...</option>
                                    {availableTypes.map(type => (
                                      <option key={type} value={type}>{formatMatchType(type)}</option>
                                    ))}
                                  </select>
                                );
                              })()}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Team Members Section */}
              <div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Team Members
              </h2>
              {activeMembers.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-6 md:p-8 text-center">
                  <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                    No team members
                  </p>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {activeMembers.map((member) => (
                      <div
                        key={member.id}
                        className="px-4 py-4 md:px-6 md:py-5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <Users className="w-5 h-5 text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                                    {getDisplayName(member.user)}
                                  </p>
                                  {member.user.ntrp_rating && (
                                    <span className="text-xs text-gray-500 dark:text-gray-500">
                                      {member.user.ntrp_rating.toFixed(1)}
                                    </span>
                                  )}
                                  {member.is_captain && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                      Captain
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 space-y-1">
                                  {member.user.email && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                      <Mail className="w-4 h-4 flex-shrink-0" />
                                      <a
                                        href={`mailto:${member.user.email}`}
                                        className="hover:text-primary-600 dark:hover:text-primary-400 truncate"
                                      >
                                        {member.user.email}
                                      </a>
                                    </div>
                                  )}
                                  {member.user.phone && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                      <Phone className="w-4 h-4 flex-shrink-0" />
                                      <a
                                        href={`tel:${member.user.phone.replace(/\D/g, '')}`}
                                        className="hover:text-primary-600 dark:hover:text-primary-400"
                                      >
                                        {formatPhoneNumber(member.user.phone)}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip portal */}
      {tooltipState && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-md px-3 py-2 shadow-lg z-[10000] max-w-xs pointer-events-none"
          style={{
            top: `${tooltipState.y}px`,
            left: `${tooltipState.x}px`,
            transform: 'translate(-50%, -100%)',
            marginTop: '-8px'
          }}
        >
          {tooltipState.text}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgb(17 24 39)'
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default TeamPage;

